/**
 * @file feedback-widget.ts
 * @description Accessible custom element Web Component (<ctt-feedback-widget>)
 * providing an in-app feedback dialog with client-side diagnostic harvesting,
 * WCAG-compliant keyboard focus trapping, and progressive submission states.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import {
  DEFAULT_FEEDBACK_PRIORITY,
  DEFAULT_VIEWPORT_PIXEL_RATIO,
  FEEDBACK_CATEGORIES,
  FEEDBACK_PRIORITIES,
  MIN_FEEDBACK_MESSAGE_LENGTH,
  MAX_FEEDBACK_MESSAGE_LENGTH,
  MAX_FEEDBACK_URL_LENGTH,
  MAX_FEEDBACK_USER_AGENT_LENGTH,
  MAX_FEEDBACK_LOG_MESSAGE_LENGTH,
  MAX_FEEDBACK_CONSOLE_LOGS_COUNT,
  type FeedbackCategory,
  type FeedbackPriority,
  type DiagnosticContext,
  type ConsoleLogEntry,
} from '@count-the-time/chronos-schema';
import { resolveInitialTheme } from '../display/theme-tokens.js';
import { authenticatedFetch, type AuthStore } from '../auth.js';

/*======================================================================
 * Element IDs & Tag Constants
 *======================================================================*/

/**
 * Canonical custom element tag name for the feedback widget.
 */
export const FEEDBACK_WIDGET_TAG_NAME = 'ctt-feedback-widget' as const;

/**
 * Centralized, typed element IDs for feedback widget components.
 */
export const FEEDBACK_WIDGET_ELEMENT_IDS = {
  overlay: 'ctt-feedback-overlay',
  dialog: 'ctt-feedback-dialog',
  title: 'ctt-feedback-title',
  description: 'ctt-feedback-description',
  closeButton: 'ctt-feedback-close-btn',
  form: 'ctt-feedback-form',
  categorySelect: 'ctt-feedback-category',
  prioritySelect: 'ctt-feedback-priority',
  messageInput: 'ctt-feedback-message',
  charCounter: 'ctt-feedback-char-counter',
  emailInput: 'ctt-feedback-email',
  diagnosticCheckbox: 'ctt-feedback-diagnostic-opt-in',
  diagnosticDetails: 'ctt-feedback-diagnostic-details',
  diagnosticSummary: 'ctt-feedback-diagnostic-summary',
  submitButton: 'ctt-feedback-submit-btn',
  cancelButton: 'ctt-feedback-cancel-btn',
  statusContainer: 'ctt-feedback-status',
  submittingSpinner: 'ctt-feedback-spinner',
  successMessage: 'ctt-feedback-success',
  successPane: 'ctt-feedback-success-pane',
  ticketIdBadge: 'ctt-feedback-ticket-id',
  copyTicketIdButton: 'ctt-feedback-copy-id-btn',
  doneButton: 'ctt-feedback-done-btn',
  errorMessage: 'ctt-feedback-error',
  retryButton: 'ctt-feedback-retry-btn',
} as const;

export type FeedbackWidgetElementId =
  (typeof FEEDBACK_WIDGET_ELEMENT_IDS)[keyof typeof FEEDBACK_WIDGET_ELEMENT_IDS];

/**
 * Canonical feedback submission endpoint.
 */
export const FEEDBACK_API_ENDPOINT = '/api/feedback' as const;

/**
 * Canonical confirmation message prompt displayed when discarding unsaved feedback.
 */
export const CONFIRM_DISCARD_FEEDBACK_MESSAGE =
  'You have unsaved feedback. Discard your feedback?' as const;

/**
 * Timeout in milliseconds before automatically dismissing success dialog.
 */
export const AUTO_DISMISS_TIMEOUT_MS = 4000 as const;

/**
 * Canonical URL query parameter key for in-app or external referrer URLs per ADR-008.
 */
export const FEEDBACK_QUERY_PARAM_REF = 'ref' as const;

/**
 * Session storage keys used for in-app page navigation and referrer tracking.
 */
export const FEEDBACK_STORAGE_KEY_CURRENT_URL = 'ctt_feedback_current_url' as const;
export const FEEDBACK_STORAGE_KEY_LAST_REFERRER = 'ctt_feedback_last_referrer' as const;

/**
 * List of sensitive query parameter and hash fragments to redact.
 */
const SENSITIVE_QUERY_KEYS: readonly string[] = [
  'token',
  'apikey',
  'api_key',
  'auth',
  'secret',
  'password',
  'bearer',
  'session',
  'jwt',
  'credential',
  'code',
  'access_token',
] as const;

/*======================================================================
 * Prefill & Harvest Options
 *======================================================================*/

/**
 * Options accepted when opening the feedback modal.
 */
export interface OpenFeedbackModalOptions {
  category?: FeedbackCategory;
  priority?: FeedbackPriority;
  message?: string;
  contactEmail?: string;
  sessionUserId?: string | null;
  referrer?: string;
  authStore?: AuthStore | null;
}

/**
 * Options accepted when harvesting client diagnostic context.
 */
export interface HarvestDiagnosticOptions {
  sessionUserId?: string | null;
  urlOverride?: string;
  referrerOverride?: string;
  userAgentOverride?: string;
  viewportOverride?: {
    width: number;
    height: number;
    pixelRatio: number;
  };
  capturedLogs?: ConsoleLogEntry[];
}

/**
 * Internal interface caching rendered DOM element references for O(1) access.
 */
interface FeedbackWidgetElements {
  overlay: HTMLElement;
  dialog: HTMLElement;
  title: HTMLElement;
  description: HTMLElement;
  closeButton: HTMLButtonElement;
  form: HTMLFormElement;
  categorySelect: HTMLSelectElement;
  prioritySelect: HTMLSelectElement;
  messageInput: HTMLTextAreaElement;
  charCounter: HTMLElement;
  emailInput: HTMLInputElement;
  diagnosticCheckbox: HTMLInputElement;
  diagnosticDetails: HTMLElement;
  diagnosticSummary: HTMLElement;
  diagPreview: HTMLElement;
  submitButton: HTMLButtonElement;
  cancelButton: HTMLButtonElement;
  statusContainer: HTMLElement;
  submittingSpinner: HTMLElement;
  successMessage: HTMLElement;
  successPane: HTMLElement;
  ticketIdBadge: HTMLElement;
  copyTicketIdButton: HTMLButtonElement;
  doneButton: HTMLButtonElement;
  errorMessage: HTMLElement;
  errorText: HTMLElement;
  retryButton: HTMLButtonElement;
}

/*======================================================================
 * Diagnostic Context Helpers
 *======================================================================*/

/**
 * Redacts sensitive authentication tokens and credentials from diagnostic URLs.
 *
 * @param rawUrl - The raw URL string to sanitize.
 * @returns Clean, sanitized URL string with sensitive query parameters and hash fragments redacted.
 */
export function sanitizeDiagnosticUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl, 'https://countthetime.com');
    const keysToRedact: string[] = [];
    parsed.searchParams.forEach((_val, key) => {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_QUERY_KEYS.some((sensitive) => lowerKey.includes(sensitive))) {
        keysToRedact.push(key);
      }
    });
    for (const key of keysToRedact) {
      parsed.searchParams.set(key, '[REDACTED]');
    }
    if (parsed.hash) {
      const lowerHash = parsed.hash.toLowerCase();
      if (SENSITIVE_QUERY_KEYS.some((sensitive) => lowerHash.includes(sensitive))) {
        parsed.hash = '#[REDACTED]';
      }
    }
    return parsed.toString().slice(0, MAX_FEEDBACK_URL_LENGTH);
  } catch {
    return rawUrl.slice(0, MAX_FEEDBACK_URL_LENGTH);
  }
}

/**
 * Redacts sensitive credentials, bearer tokens, and secrets from captured console logs.
 *
 * @param logs - Array of raw diagnostic console log entries.
 * @returns Sanitized log entries with sensitive data replaced with redacted markers.
 */
export function sanitizeDiagnosticLogs(logs: ConsoleLogEntry[]): ConsoleLogEntry[] {
  return logs.map((entry) => {
    let msg = entry.message;
    // Redact Bearer tokens
    msg = msg.replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]');
    // Redact JWT patterns
    msg = msg.replace(/eyJ[A-Za-z0-9-_]+\.eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+/g, '[REDACTED_JWT]');
    // Redact passwords and secrets
    msg = msg.replace(
      /(?:password|secret|token|apikey)\s*[:=]\s*["']?([^"',\s]+)["']?/gi,
      (match) => {
        const colonIdx = match.indexOf(':');
        const eqIdx = match.indexOf('=');
        const sep = colonIdx !== -1 ? ':' : '=';
        const key = match.slice(0, Math.max(colonIdx, eqIdx)).trim();
        return `${key}${sep} "[REDACTED]"`;
      },
    );

    return {
      ...entry,
      message: msg.slice(0, MAX_FEEDBACK_LOG_MESSAGE_LENGTH),
    };
  });
}

/**
 * Records page navigation transitions in sessionStorage to accurately track
 * in-app referrers even when browsers omit HTTP Referer or document.referrer.
 *
 * @param currentHref - The current location href (defaults to window.location.href).
 */
export function recordPageNavigation(
  currentHref = typeof window !== 'undefined' ? window.location.href : '',
): void {
  if (typeof window === 'undefined' || !window.sessionStorage || !currentHref) {
    return;
  }
  try {
    const previousCurrent = window.sessionStorage.getItem(FEEDBACK_STORAGE_KEY_CURRENT_URL);
    if (previousCurrent) {
      try {
        const parsedPrev = new URL(previousCurrent, 'https://countthetime.com');
        const parsedCurr = new URL(currentHref, 'https://countthetime.com');
        if (
          parsedPrev.origin !== parsedCurr.origin ||
          parsedPrev.pathname !== parsedCurr.pathname
        ) {
          window.sessionStorage.setItem(FEEDBACK_STORAGE_KEY_LAST_REFERRER, previousCurrent);
        }
      } catch {
        window.sessionStorage.setItem(FEEDBACK_STORAGE_KEY_LAST_REFERRER, previousCurrent);
      }
    }
    window.sessionStorage.setItem(FEEDBACK_STORAGE_KEY_CURRENT_URL, currentHref);
  } catch {
    // Gracefully handle storage exceptions (e.g. quota or sandbox errors)
  }
}

/**
 * Resolves the client referrer URL using a multi-tiered diagnostic cascade:
 * 1. Explicit override (options.referrerOverride)
 * 2. URL search parameters (canonical ?ref= per ADR-008)
 * 3. document.referrer (if present, valid, and not pointing to the exact same page)
 * 4. In-app navigation history from sessionStorage (FEEDBACK_STORAGE_KEY_LAST_REFERRER)
 *
 * @param currentUrl - The current page URL.
 * @param options - Harvesting options containing optional overrides.
 * @returns Sanitized referrer URL, or undefined if no external or prior in-app referrer is available.
 */
export function resolveReferrerUrl(
  currentUrl: string,
  options?: HarvestDiagnosticOptions,
): string | undefined {
  let rawReferrer = options?.referrerOverride?.trim();

  // Tier 2: Check URL search parameters on the current URL (canonical ?ref=)
  if (!rawReferrer) {
    try {
      const parsedCurrent = new URL(currentUrl, 'https://countthetime.com');
      const refParam = parsedCurrent.searchParams.get(FEEDBACK_QUERY_PARAM_REF);
      if (refParam) {
        const parsedFrom = new URL(refParam, parsedCurrent.origin);
        rawReferrer = parsedFrom.href;
      }
    } catch {
      // ignore parsing error
    }
  }

  // Tier 3: document.referrer (if present and non-empty)
  if (!rawReferrer && typeof document !== 'undefined' && document.referrer) {
    const docRef = document.referrer.trim();
    if (docRef) {
      try {
        const parsedDocRef = new URL(docRef, 'https://countthetime.com');
        const parsedCurrent = new URL(currentUrl, 'https://countthetime.com');
        if (
          parsedDocRef.origin !== parsedCurrent.origin ||
          parsedDocRef.pathname !== parsedCurrent.pathname
        ) {
          rawReferrer = docRef;
        }
      } catch {
        rawReferrer = docRef;
      }
    }
  }

  // Tier 4: In-app session storage history
  if (!rawReferrer && typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const storedLast = window.sessionStorage.getItem(FEEDBACK_STORAGE_KEY_LAST_REFERRER);
      if (storedLast) {
        try {
          const parsedStored = new URL(storedLast, 'https://countthetime.com');
          const parsedCurrent = new URL(currentUrl, 'https://countthetime.com');
          if (
            parsedStored.origin !== parsedCurrent.origin ||
            parsedStored.pathname !== parsedCurrent.pathname
          ) {
            rawReferrer = storedLast;
          }
        } catch {
          rawReferrer = storedLast;
        }
      }
    } catch {
      // ignore storage access errors
    }
  }

  return rawReferrer ? sanitizeDiagnosticUrl(rawReferrer) : undefined;
}

/**
 * Harvests client diagnostic telemetry including current URL, viewport geometry,
 * user agent, and optional console error logs.
 *
 * @param options - Harvesting options and environment overrides.
 * @returns Validated diagnostic telemetry payload.
 */
export function harvestDiagnosticContext(options?: HarvestDiagnosticOptions): DiagnosticContext {
  const rawUrl = options?.urlOverride ?? window.location.href;
  const url = sanitizeDiagnosticUrl(rawUrl);

  const referrer = resolveReferrerUrl(rawUrl, options);

  const width = options?.viewportOverride?.width ?? window.innerWidth;
  const height = options?.viewportOverride?.height ?? window.innerHeight;
  const pixelRatio = options?.viewportOverride?.pixelRatio ?? window.devicePixelRatio;

  const userAgent = (options?.userAgentOverride ?? navigator.userAgent).slice(
    0,
    MAX_FEEDBACK_USER_AGENT_LENGTH,
  );

  const sanitizedLogs = options?.capturedLogs
    ? sanitizeDiagnosticLogs(options.capturedLogs)
    : undefined;

  const diag: DiagnosticContext = {
    url,
    viewport: {
      width: Math.max(0, Math.floor(width)),
      height: Math.max(0, Math.floor(height)),
      pixelRatio: pixelRatio > 0 ? pixelRatio : DEFAULT_VIEWPORT_PIXEL_RATIO,
    },
    userAgent,
  };

  if (referrer) {
    diag.referrer = referrer;
  }

  if (sanitizedLogs && sanitizedLogs.length > 0) {
    diag.consoleLogs = sanitizedLogs.slice(0, MAX_FEEDBACK_CONSOLE_LOGS_COUNT);
  }

  if (options?.sessionUserId !== undefined) {
    diag.sessionUserId = options.sessionUserId;
  }

  return diag;
}

/*======================================================================
 * Component Styles
 *======================================================================*/

const COMPONENT_STYLES = `
:host {
  display: block;
}

.overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
  box-sizing: border-box;
}

.dialog {
  background-color: #ffffff;
  color: #0f172a;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  box-sizing: border-box;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #64748b;
  padding: 4px 8px;
  border-radius: 6px;
  line-height: 1;
}

.close-btn:hover {
  color: #0f172a;
  background-color: #f1f5f9;
}

.dialog-desc {
  margin: 0 0 16px 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.4;
}

.form-group {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  box-sizing: border-box;
  color: #0f172a;
  background-color: #ffffff;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
  border-color: #2563eb;
}

.form-textarea {
  resize: vertical;
  min-height: 90px;
}

.char-counter {
  font-size: 0.75rem;
  color: #64748b;
  text-align: right;
  margin-top: 4px;
}

.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8125rem;
  color: #475569;
}

.checkbox-row input[type="checkbox"] {
  margin-top: 2px;
}

.diagnostic-details {
  margin-top: 8px;
  font-size: 0.75rem;
  color: #64748b;
}

.diagnostic-summary {
  cursor: pointer;
  font-weight: 500;
}

.diag-preview {
  margin-top: 6px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.7rem;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 120px;
  overflow-y: auto;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-secondary:hover:not([disabled]) {
  background: #e2e8f0;
}

.btn-primary {
  background: #2563eb;
  color: #ffffff;
}

.btn-primary:hover:not([disabled]) {
  background: #1d4ed8;
}

.btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.status-container {
  margin-bottom: 12px;
}

.spinner {
  display: inline-block;
  padding: 8px 12px;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  width: 100%;
  box-sizing: border-box;
}

.success-banner {
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.4;
}

.success-pane {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.ticket-badge-container {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #334155;
}

.ticket-id-badge {
  background: #f1f5f9;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  font-weight: 600;
  user-select: all;
}

.error-banner {
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 6px;
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.retry-btn {
  align-self: flex-start;
  padding: 4px 10px;
  background: #dc2626;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.retry-btn:hover {
  background: #b91c1c;
}

:host([data-theme="dark"]) .dialog,
:host-context([data-theme="dark"]) .dialog,
:host-context([theme="dark"]) .dialog,
:host-context(.ctt-theme-dark) .dialog {
  background-color: #1e293b;
  color: #f8fafc;
  border-color: #334155;
}
:host([data-theme="dark"]) .dialog-title,
:host-context([data-theme="dark"]) .dialog-title,
:host-context([theme="dark"]) .dialog-title,
:host-context(.ctt-theme-dark) .dialog-title {
  color: #f8fafc;
}
:host([data-theme="dark"]) .dialog-desc,
:host-context([data-theme="dark"]) .dialog-desc,
:host-context([theme="dark"]) .dialog-desc,
:host-context(.ctt-theme-dark) .dialog-desc {
  color: #94a3b8;
}
:host([data-theme="dark"]) .form-label,
:host-context([data-theme="dark"]) .form-label,
:host-context([theme="dark"]) .form-label,
:host-context(.ctt-theme-dark) .form-label {
  color: #cbd5e1;
}
:host([data-theme="dark"]) .form-input,
:host([data-theme="dark"]) .form-select,
:host([data-theme="dark"]) .form-textarea,
:host-context([data-theme="dark"]) .form-input,
:host-context([data-theme="dark"]) .form-select,
:host-context([data-theme="dark"]) .form-textarea,
:host-context([theme="dark"]) .form-input,
:host-context([theme="dark"]) .form-select,
:host-context([theme="dark"]) .form-textarea,
:host-context(.ctt-theme-dark) .form-input,
:host-context(.ctt-theme-dark) .form-select,
:host-context(.ctt-theme-dark) .form-textarea {
  background-color: #0f172a;
  color: #f8fafc;
  border-color: #334155;
}
:host([data-theme="dark"]) .diag-preview,
:host-context([data-theme="dark"]) .diag-preview,
:host-context([theme="dark"]) .diag-preview,
:host-context(.ctt-theme-dark) .diag-preview {
  background: #0f172a;
  color: #cbd5e1;
}
:host([data-theme="dark"]) .btn-secondary,
:host-context([data-theme="dark"]) .btn-secondary,
:host-context([theme="dark"]) .btn-secondary,
:host-context(.ctt-theme-dark) .btn-secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}
:host([data-theme="dark"]) .success-banner,
:host-context([data-theme="dark"]) .success-banner,
:host-context([theme="dark"]) .success-banner,
:host-context(.ctt-theme-dark) .success-banner {
  background: #064e3b;
  border-color: #065f46;
  color: #a7f3d0;
}
:host([data-theme="dark"]) .error-banner,
:host-context([data-theme="dark"]) .error-banner,
:host-context([theme="dark"]) .error-banner,
:host-context(.ctt-theme-dark) .error-banner {
  background: #450a0a;
  border-color: #7f1d1d;
  color: #fecaca;
}

:host([data-theme="dark"]) .ticket-badge-container,
:host-context([data-theme="dark"]) .ticket-badge-container,
:host-context([theme="dark"]) .ticket-badge-container,
:host-context(.ctt-theme-dark) .ticket-badge-container {
  color: #94a3b8;
}
:host([data-theme="dark"]) .ticket-id-badge,
:host-context([data-theme="dark"]) .ticket-id-badge,
:host-context([theme="dark"]) .ticket-id-badge,
:host-context(.ctt-theme-dark) .ticket-id-badge {
  background: #0f172a;
  color: #f8fafc;
  border-color: #334155;
}

@media (prefers-color-scheme: dark) {
  .dialog {
    background-color: #1e293b;
    color: #f8fafc;
    border-color: #334155;
  }
  .dialog-title {
    color: #f8fafc;
  }
  .dialog-desc {
    color: #94a3b8;
  }
  .form-label {
    color: #cbd5e1;
  }
  .form-input,
  .form-select,
  .form-textarea {
    background-color: #0f172a;
    color: #f8fafc;
    border-color: #334155;
  }
  .diag-preview {
    background: #0f172a;
    color: #cbd5e1;
  }
  .btn-secondary {
    background: #334155;
    color: #e2e8f0;
    border-color: #475569;
  }
  .ticket-badge-container {
    color: #94a3b8;
  }
  .ticket-id-badge {
    background: #0f172a;
    color: #f8fafc;
    border-color: #334155;
  }
  .success-banner {
    background: #064e3b;
    border-color: #065f46;
    color: #a7f3d0;
  }
  .error-banner {
    background: #450a0a;
    border-color: #7f1d1d;
    color: #fecaca;
  }
}
`;

/*======================================================================
 * Custom Element Implementation
 *======================================================================*/

/**
 * Accessible in-app feedback dialog Web Component.
 */
export class CttFeedbackWidget extends HTMLElement {
  private _isOpen = false;
  private _elements: FeedbackWidgetElements | null = null;
  private _previouslyFocusedElement: HTMLElement | null = null;
  private _autoDismissTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _copyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _currentTicketId: string | null = null;
  private _sessionUserId: string | null = null;
  private _referrerOverride: string | null = null;
  private _authStore: AuthStore | null = null;
  private _themeListener: ((e: Event) => void) | null = null;

  /**
   * Optional authentication store instance used to inject Bearer tokens during feedback submissions.
   */
  public get authStore(): AuthStore | null {
    return this._authStore;
  }

  public set authStore(store: AuthStore | null) {
    this._authStore = store;
  }

  /**
   * Optional custom confirmation callback used for testing or UI dialog customization.
   * Return true to proceed with discarding unsaved feedback, false to abort dismissal.
   */
  public confirmDiscardFn: (() => boolean) | null = null;

  /**
   * Determines whether the feedback form contains unsaved user-authored text.
   *
   * @returns True if the message textarea contains non-empty trimmed text.
   */
  private isDirty(): boolean {
    return this.elements.messageInput.value.trim().length > 0;
  }

  /**
   * Prompts the user before discarding unsaved feedback if the form is dirty.
   * Uses confirmDiscardFn if defined, falling back to window.confirm.
   *
   * @returns True if the discard is allowed or form is clean; false if cancelled.
   */
  private confirmDiscardIfDirty(): boolean {
    if (!this.isDirty()) {
      return true;
    }
    if (this.confirmDiscardFn) {
      return this.confirmDiscardFn();
    }
    return typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm(CONFIRM_DISCARD_FEEDBACK_MESSAGE)
      : true;
  }

  // Tracked bound event listeners for memory leak prevention
  private readonly _handleDocumentKeyDown = (evt: Event): void => {
    const keyEvent = evt as KeyboardEvent;
    if (keyEvent.key === 'Escape' && this._isOpen) {
      if (!this.confirmDiscardIfDirty()) {
        return;
      }
      this.closeModal();
    }
  };

  private readonly _handleOpenFeedbackEvent = (evt: Event): void => {
    const customEvt = evt as CustomEvent<OpenFeedbackModalOptions>;
    this.openModal(customEvt?.detail);
  };

  private readonly _handleDialogKeyDown = (evt: Event): void => {
    const keyEvent = evt as KeyboardEvent;
    if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault?.();
      if (!this.confirmDiscardIfDirty()) {
        return;
      }
      this.closeModal();
      return;
    }

    if (keyEvent.key === 'Tab') {
      this.handleFocusTrap(keyEvent);
    }
  };

  private readonly _handleOverlayClick = (evt: Event): void => {
    if (evt.target === this.elements.overlay) {
      if (!this.confirmDiscardIfDirty()) {
        return;
      }
      this.closeModal();
    }
  };

  private readonly _handleCloseClick = (): void => {
    if (!this.confirmDiscardIfDirty()) {
      return;
    }
    this.closeModal();
  };

  private readonly _handleCancelClick = (): void => {
    if (!this.confirmDiscardIfDirty()) {
      return;
    }
    this.closeModal();
  };

  private readonly _handleMessageInput = (): void => {
    this.updateCharCounterAndValidation();
  };

  private readonly _handleCategoryChange = (): void => {
    this.updateCharCounterAndValidation();
  };

  private readonly _handleFormSubmit = (evt: Event): void => {
    evt.preventDefault?.();
    void this.submitFeedback();
  };

  private readonly _handleRetryClick = (): void => {
    void this.submitFeedback();
  };

  private clearCopyTimeout(): void {
    if (this._copyTimeoutId !== null) {
      clearTimeout(this._copyTimeoutId);
      this._copyTimeoutId = null;
    }
  }

  private readonly _handleCopyTicketIdClick = (): void => {
    const el = this.elements;
    const ticketId = this._currentTicketId || el.ticketIdBadge.textContent;
    if (ticketId && typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(ticketId);
    }

    el.copyTicketIdButton.textContent = 'Copied!';

    this.clearCopyTimeout();
    this._copyTimeoutId = setTimeout(() => {
      if (this._elements) {
        this._elements.copyTicketIdButton.textContent = 'Copy Ticket ID';
      }
      this._copyTimeoutId = null;
    }, 2000);
  };

  private readonly _handleDoneClick = (): void => {
    this.closeModal();
  };

  private get elements(): FeedbackWidgetElements {
    if (!this._elements) {
      this.renderDOM();
    }
    return this._elements as FeedbackWidgetElements;
  }

  /**
   * Opens the feedback modal dialog and optionally pre-fills input fields.
   *
   * @param options - Optional initial field values and session metadata.
   */
  public openModal(options?: OpenFeedbackModalOptions): void {
    const el = this.elements;
    this.bindEventListeners();

    this._previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;

    el.categorySelect.value = options?.category ?? '';
    el.prioritySelect.value = options?.priority ?? DEFAULT_FEEDBACK_PRIORITY;
    el.messageInput.value = options?.message ?? '';
    el.emailInput.value = options?.contactEmail ?? '';

    if (options?.sessionUserId !== undefined) {
      this._sessionUserId = options.sessionUserId;
    }

    if (options?.referrer !== undefined) {
      this._referrerOverride = options.referrer;
    }

    if (options?.authStore !== undefined) {
      this._authStore = options.authStore;
    }

    // Reset status views
    el.title.textContent = 'Send Feedback';
    el.description.style.display = 'block';
    el.submittingSpinner.style.display = 'none';
    el.successMessage.style.display = 'none';
    el.successPane.style.display = 'none';
    el.errorMessage.style.display = 'none';
    el.form.style.display = 'block';
    el.copyTicketIdButton.textContent = 'Copy Ticket ID';
    this._currentTicketId = null;
    this.clearCopyTimeout();

    this.enableFormControls();
    this.updateCharCounterAndValidation();
    this.updateDiagnosticPreview();

    el.overlay.style.display = 'flex';
    this._isOpen = true;

    el.closeButton.focus();
  }

  /**
   * Closes the feedback modal dialog, resets form state, and restores focus to trigger.
   */
  public closeModal(): void {
    if (this._autoDismissTimeoutId !== null) {
      clearTimeout(this._autoDismissTimeoutId);
      this._autoDismissTimeoutId = null;
    }
    this.clearCopyTimeout();

    if (this._elements) {
      this._elements.overlay.style.display = 'none';
    }
    this._isOpen = false;
    this._referrerOverride = null;

    if (this._previouslyFocusedElement) {
      this._previouslyFocusedElement.focus();
      this._previouslyFocusedElement = null;
    }
  }

  /**
   * Whether the feedback dialog is currently open and visible to users.
   */
  public get isOpen(): boolean {
    return this._isOpen;
  }

  /**
   * Transitions the widget into the submitting state with a loading spinner.
   */
  public showSubmittingState(): void {
    const el = this.elements;
    this.disableFormControls();
    el.submittingSpinner.style.display = 'inline-block';
    el.errorMessage.style.display = 'none';
    el.successMessage.style.display = 'none';
    el.successPane.style.display = 'none';
  }

  /**
   * Displays an error alert banner with optional retry capability.
   *
   * @param errorMessage - Error message to display.
   */
  public showErrorMessage(errorMessage: string): void {
    const el = this.elements;
    el.submittingSpinner.style.display = 'none';
    this.enableFormControls();
    el.errorText.textContent = errorMessage;
    el.errorMessage.style.display = 'flex';
  }

  /**
   * Transitions the widget into the success confirmation pane displaying the reference ticket ID.
   *
   * @param ticketId - Generated support ticket identifier (e.g., CTT-FB-A41920).
   */
  public showSuccessConfirmation(ticketId: string): void {
    const el = this.elements;
    this._currentTicketId = ticketId;
    el.submittingSpinner.style.display = 'none';
    el.errorMessage.style.display = 'none';
    el.title.textContent = 'Feedback Received';
    el.description.style.display = 'none';
    el.form.style.display = 'none';
    el.ticketIdBadge.textContent = ticketId;
    el.successMessage.textContent = `Thank you! Your feedback has been received (Reference Ticket: ${ticketId}). Our team will review it promptly.`;
    el.successMessage.style.display = 'block';
    el.successPane.style.display = 'flex';
    el.doneButton.focus();
  }

  /**
   * Gathers client diagnostic context from the current browser session.
   *
   * @returns Telemetry context object.
   */
  public harvestDiagnosticContext(): DiagnosticContext {
    return harvestDiagnosticContext({
      sessionUserId: this._sessionUserId,
      referrerOverride: this._referrerOverride ?? undefined,
    });
  }

  /**
   * Submits feedback payload asynchronously to `/api/feedback`.
   *
   * @returns Promise resolving to true if submission succeeded, false otherwise.
   */
  public async submitFeedback(): Promise<boolean> {
    const el = this.elements;

    const category = el.categorySelect.value.trim();
    const message = el.messageInput.value.trim();
    if (!category || message.length < MIN_FEEDBACK_MESSAGE_LENGTH) {
      this.updateCharCounterAndValidation();
      return false;
    }

    this.showSubmittingState();

    const payload: Record<string, unknown> = {
      category: el.categorySelect.value,
      priority: el.prioritySelect.value,
      message,
    };

    const email = el.emailInput.value.trim();
    if (email) {
      payload.contactEmail = email;
    }

    if (el.diagnosticCheckbox.checked) {
      payload.metadata = this.harvestDiagnosticContext();
    }

    try {
      const response = await authenticatedFetch(
        FEEDBACK_API_ENDPOINT,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
        this._authStore,
      );

      if (!response.ok) {
        let errorMsg = `Submission failed (${response.status}). Please try again.`;
        try {
          const errData = (await response.json()) as { error?: string; message?: string };
          if (errData?.message) {
            errorMsg = errData.message;
          } else if (errData?.error) {
            errorMsg = errData.error;
          }
        } catch {
          // Keep default status message
        }

        this.showErrorMessage(errorMsg);
        return false;
      }

      const data = (await response.json()) as { success?: boolean; ticketId?: string };
      const ticketId = data?.ticketId ?? 'TICKET-PENDING';

      this.showSuccessConfirmation(ticketId);
      return true;
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Network error. Please check your connection.';
      this.showErrorMessage(msg);
      return false;
    }
  }

  /**
   * Web Component lifecycle callback invoked when mounted into the DOM.
   */
  public connectedCallback(): void {
    recordPageNavigation();
    if (!this.hasAttribute('data-theme')) {
      this.setAttribute('data-theme', resolveInitialTheme());
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this._themeListener = (e: Event) => {
        const theme = (e as CustomEvent)?.detail?.theme;
        if (theme === 'dark' || theme === 'light') {
          this.setAttribute('data-theme', theme);
        }
      };
      window.addEventListener('ctt-theme-change', this._themeListener);
    }

    this.renderDOM();
    this.bindEventListeners();
  }

  /**
   * Web Component lifecycle callback invoked when disconnected from the DOM.
   */
  public disconnectedCallback(): void {
    this.unbindEventListeners();
    if (
      this._themeListener &&
      typeof window !== 'undefined' &&
      typeof window.removeEventListener === 'function'
    ) {
      window.removeEventListener('ctt-theme-change', this._themeListener);
      this._themeListener = null;
    }
    if (this._autoDismissTimeoutId !== null) {
      clearTimeout(this._autoDismissTimeoutId);
      this._autoDismissTimeoutId = null;
    }
    this.clearCopyTimeout();
  }

  /*======================================================================
   * Private DOM Helpers & Event Management
   *======================================================================*/

  private renderDOM(): void {
    if (typeof this.attachShadow === 'function' && !this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }
    if (this._elements || !this.shadowRoot) return;

    // Reset shadow root content
    this.shadowRoot.innerHTML = '';

    const styleEl = document.createElement('style');
    styleEl.textContent = COMPONENT_STYLES;
    this.shadowRoot.appendChild(styleEl);

    // Overlay
    const overlay = document.createElement('div');
    overlay.id = FEEDBACK_WIDGET_ELEMENT_IDS.overlay;
    overlay.className = 'overlay';
    overlay.style.display = 'none';

    // Dialog Card
    const dialog = document.createElement('div');
    dialog.id = FEEDBACK_WIDGET_ELEMENT_IDS.dialog;
    dialog.className = 'dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', FEEDBACK_WIDGET_ELEMENT_IDS.title);
    dialog.setAttribute('aria-describedby', FEEDBACK_WIDGET_ELEMENT_IDS.description);

    // Header
    const header = document.createElement('div');
    header.className = 'dialog-header';

    const title = document.createElement('h2');
    title.id = FEEDBACK_WIDGET_ELEMENT_IDS.title;
    title.className = 'dialog-title';
    title.textContent = 'Send Feedback';
    header.appendChild(title);

    const closeButton = document.createElement('button') as HTMLButtonElement;
    closeButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.closeButton;
    closeButton.type = 'button';
    closeButton.className = 'close-btn';
    closeButton.setAttribute('aria-label', 'Close feedback dialog');
    closeButton.textContent = '✕';
    header.appendChild(closeButton);

    dialog.appendChild(header);

    // Description
    const description = document.createElement('p');
    description.id = FEEDBACK_WIDGET_ELEMENT_IDS.description;
    description.className = 'dialog-desc';
    description.textContent =
      'Help us improve CountTheTime! Share your thoughts, report issues, or suggest new features.';
    dialog.appendChild(description);

    // Status container
    const statusContainer = document.createElement('div');
    statusContainer.id = FEEDBACK_WIDGET_ELEMENT_IDS.statusContainer;
    statusContainer.className = 'status-container';
    statusContainer.setAttribute('aria-live', 'polite');

    const submittingSpinner = document.createElement('div');
    submittingSpinner.id = FEEDBACK_WIDGET_ELEMENT_IDS.submittingSpinner;
    submittingSpinner.className = 'spinner';
    submittingSpinner.style.display = 'none';
    submittingSpinner.textContent = 'Submitting feedback...';
    statusContainer.appendChild(submittingSpinner);

    const successMessage = document.createElement('div');
    successMessage.id = FEEDBACK_WIDGET_ELEMENT_IDS.successMessage;
    successMessage.className = 'success-banner';
    successMessage.style.display = 'none';
    statusContainer.appendChild(successMessage);

    const errorMessage = document.createElement('div');
    errorMessage.id = FEEDBACK_WIDGET_ELEMENT_IDS.errorMessage;
    errorMessage.className = 'error-banner';
    errorMessage.setAttribute('role', 'alert');
    errorMessage.style.display = 'none';

    const errorText = document.createElement('span');
    errorMessage.appendChild(errorText);

    const retryButton = document.createElement('button') as HTMLButtonElement;
    retryButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.retryButton;
    retryButton.type = 'button';
    retryButton.className = 'retry-btn';
    retryButton.textContent = 'Retry';
    errorMessage.appendChild(retryButton);

    statusContainer.appendChild(errorMessage);
    dialog.appendChild(statusContainer);

    // Form
    const form = document.createElement('form') as HTMLFormElement;
    form.id = FEEDBACK_WIDGET_ELEMENT_IDS.form;

    // Category Select
    const catGroup = document.createElement('div');
    catGroup.className = 'form-group';
    const catLabel = document.createElement('label');
    catLabel.htmlFor = FEEDBACK_WIDGET_ELEMENT_IDS.categorySelect;
    catLabel.setAttribute('for', FEEDBACK_WIDGET_ELEMENT_IDS.categorySelect);
    catLabel.className = 'form-label';
    catLabel.textContent = 'Feedback Category';
    catGroup.appendChild(catLabel);

    const categorySelect = document.createElement('select') as HTMLSelectElement;
    categorySelect.id = FEEDBACK_WIDGET_ELEMENT_IDS.categorySelect;
    categorySelect.className = 'form-select';

    const placeholderOpt = document.createElement('option');
    placeholderOpt.value = '';
    placeholderOpt.textContent = 'Select a category...';
    placeholderOpt.setAttribute('disabled', 'true');
    placeholderOpt.setAttribute('selected', 'true');
    categorySelect.appendChild(placeholderOpt);

    for (const cat of FEEDBACK_CATEGORIES) {
      const opt = document.createElement('option');
      opt.value = cat;
      opt.textContent = cat.charAt(0).toUpperCase() + cat.slice(1);
      categorySelect.appendChild(opt);
    }
    catGroup.appendChild(categorySelect);
    form.appendChild(catGroup);

    // Priority Select
    const priGroup = document.createElement('div');
    priGroup.className = 'form-group';
    const priLabel = document.createElement('label');
    priLabel.htmlFor = FEEDBACK_WIDGET_ELEMENT_IDS.prioritySelect;
    priLabel.setAttribute('for', FEEDBACK_WIDGET_ELEMENT_IDS.prioritySelect);
    priLabel.className = 'form-label';
    priLabel.textContent = 'Priority';
    priGroup.appendChild(priLabel);

    const prioritySelect = document.createElement('select') as HTMLSelectElement;
    prioritySelect.id = FEEDBACK_WIDGET_ELEMENT_IDS.prioritySelect;
    prioritySelect.className = 'form-select';
    for (const pri of FEEDBACK_PRIORITIES) {
      const opt = document.createElement('option');
      opt.value = pri;
      opt.textContent = pri.charAt(0).toUpperCase() + pri.slice(1);
      if (pri === DEFAULT_FEEDBACK_PRIORITY) {
        opt.selected = true;
      }
      prioritySelect.appendChild(opt);
    }
    priGroup.appendChild(prioritySelect);
    form.appendChild(priGroup);

    // Message Input
    const msgGroup = document.createElement('div');
    msgGroup.className = 'form-group';
    const msgLabel = document.createElement('label');
    msgLabel.htmlFor = FEEDBACK_WIDGET_ELEMENT_IDS.messageInput;
    msgLabel.setAttribute('for', FEEDBACK_WIDGET_ELEMENT_IDS.messageInput);
    msgLabel.className = 'form-label';
    msgLabel.textContent = `Message (min ${MIN_FEEDBACK_MESSAGE_LENGTH} characters)`;
    msgGroup.appendChild(msgLabel);

    const messageInput = document.createElement('textarea') as HTMLTextAreaElement;
    messageInput.id = FEEDBACK_WIDGET_ELEMENT_IDS.messageInput;
    messageInput.className = 'form-textarea';
    messageInput.rows = 4;
    messageInput.placeholder =
      'Summarize the issue on the first line.\n\nDescribe what happened, what you expected, and any reproduction steps...';
    msgGroup.appendChild(messageInput);

    const charCounter = document.createElement('div');
    charCounter.id = FEEDBACK_WIDGET_ELEMENT_IDS.charCounter;
    charCounter.className = 'char-counter';
    charCounter.textContent = `0 / ${MAX_FEEDBACK_MESSAGE_LENGTH}`;
    msgGroup.appendChild(charCounter);
    form.appendChild(msgGroup);

    // Email Input
    const emailGroup = document.createElement('div');
    emailGroup.className = 'form-group';
    const emailLabel = document.createElement('label');
    emailLabel.htmlFor = FEEDBACK_WIDGET_ELEMENT_IDS.emailInput;
    emailLabel.setAttribute('for', FEEDBACK_WIDGET_ELEMENT_IDS.emailInput);
    emailLabel.className = 'form-label';
    emailLabel.textContent = 'Contact Email (optional)';
    emailGroup.appendChild(emailLabel);

    const emailInput = document.createElement('input') as HTMLInputElement;
    emailInput.id = FEEDBACK_WIDGET_ELEMENT_IDS.emailInput;
    emailInput.type = 'email';
    emailInput.className = 'form-input';
    emailInput.placeholder = 'you@example.com';
    emailGroup.appendChild(emailInput);
    form.appendChild(emailGroup);

    // Diagnostic Checkbox & Details
    const diagGroup = document.createElement('div');
    diagGroup.className = 'form-group';

    const diagRow = document.createElement('div');
    diagRow.className = 'checkbox-row';

    const diagnosticCheckbox = document.createElement('input') as HTMLInputElement;
    diagnosticCheckbox.id = FEEDBACK_WIDGET_ELEMENT_IDS.diagnosticCheckbox;
    diagnosticCheckbox.type = 'checkbox';
    diagnosticCheckbox.checked = true;
    diagRow.appendChild(diagnosticCheckbox);

    const diagLabel = document.createElement('label');
    diagLabel.htmlFor = FEEDBACK_WIDGET_ELEMENT_IDS.diagnosticCheckbox;
    diagLabel.setAttribute('for', FEEDBACK_WIDGET_ELEMENT_IDS.diagnosticCheckbox);
    diagLabel.textContent = 'Include technical details (page URL, browser info, screen size)';
    diagRow.appendChild(diagLabel);
    diagGroup.appendChild(diagRow);

    const diagnosticDetails = document.createElement('details');
    diagnosticDetails.id = FEEDBACK_WIDGET_ELEMENT_IDS.diagnosticDetails;
    diagnosticDetails.className = 'diagnostic-details';

    const diagnosticSummary = document.createElement('summary');
    diagnosticSummary.id = FEEDBACK_WIDGET_ELEMENT_IDS.diagnosticSummary;
    diagnosticSummary.className = 'diagnostic-summary';
    diagnosticSummary.textContent = 'Preview technical details';
    diagnosticDetails.appendChild(diagnosticSummary);

    const diagPreview = document.createElement('pre');
    diagPreview.className = 'diag-preview';
    diagnosticDetails.appendChild(diagPreview);
    diagGroup.appendChild(diagnosticDetails);

    form.appendChild(diagGroup);

    // Action Buttons
    const actionsRow = document.createElement('div');
    actionsRow.className = 'dialog-actions';

    const cancelButton = document.createElement('button') as HTMLButtonElement;
    cancelButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.cancelButton;
    cancelButton.type = 'button';
    cancelButton.className = 'btn btn-secondary';
    cancelButton.textContent = 'Cancel';
    actionsRow.appendChild(cancelButton);

    const submitButton = document.createElement('button') as HTMLButtonElement;
    submitButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.submitButton;
    submitButton.type = 'submit';
    submitButton.className = 'btn btn-primary';
    submitButton.textContent = 'Submit Feedback';
    submitButton.setAttribute('disabled', 'true');
    actionsRow.appendChild(submitButton);

    form.appendChild(actionsRow);
    dialog.appendChild(form);

    // Success Pane (Modal Confirmation)
    const successPane = document.createElement('div');
    successPane.id = FEEDBACK_WIDGET_ELEMENT_IDS.successPane;
    successPane.className = 'success-pane';
    successPane.style.display = 'none';

    const ticketRow = document.createElement('div');
    ticketRow.className = 'ticket-badge-container';

    const ticketLabel = document.createElement('span');
    ticketLabel.textContent = 'Reference Ticket: ';
    ticketRow.appendChild(ticketLabel);

    const ticketIdBadge = document.createElement('code');
    ticketIdBadge.id = FEEDBACK_WIDGET_ELEMENT_IDS.ticketIdBadge;
    ticketIdBadge.className = 'ticket-id-badge';
    ticketRow.appendChild(ticketIdBadge);

    successPane.appendChild(ticketRow);

    const successActions = document.createElement('div');
    successActions.className = 'dialog-actions';

    const copyTicketIdButton = document.createElement('button') as HTMLButtonElement;
    copyTicketIdButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.copyTicketIdButton;
    copyTicketIdButton.type = 'button';
    copyTicketIdButton.className = 'btn btn-secondary';
    copyTicketIdButton.textContent = 'Copy Ticket ID';
    successActions.appendChild(copyTicketIdButton);

    const doneButton = document.createElement('button') as HTMLButtonElement;
    doneButton.id = FEEDBACK_WIDGET_ELEMENT_IDS.doneButton;
    doneButton.type = 'button';
    doneButton.className = 'btn btn-primary';
    doneButton.textContent = 'Done';
    successActions.appendChild(doneButton);

    successPane.appendChild(successActions);
    dialog.appendChild(successPane);

    overlay.appendChild(dialog);
    this.shadowRoot.appendChild(overlay);

    this._elements = {
      overlay,
      dialog,
      title,
      description,
      closeButton,
      form,
      categorySelect,
      prioritySelect,
      messageInput,
      charCounter,
      emailInput,
      diagnosticCheckbox,
      diagnosticDetails,
      diagnosticSummary,
      diagPreview,
      submitButton,
      cancelButton,
      statusContainer,
      submittingSpinner,
      successMessage,
      successPane,
      ticketIdBadge,
      copyTicketIdButton,
      doneButton,
      errorMessage,
      errorText,
      retryButton,
    };
  }

  private bindEventListeners(): void {
    const el = this.elements;

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this._handleDocumentKeyDown);
      document.addEventListener('ctt:open-feedback', this._handleOpenFeedbackEvent);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('ctt:open-feedback', this._handleOpenFeedbackEvent);
    }

    el.dialog.addEventListener('keydown', this._handleDialogKeyDown);
    el.overlay.addEventListener('click', this._handleOverlayClick);
    el.closeButton.addEventListener('click', this._handleCloseClick);
    el.cancelButton.addEventListener('click', this._handleCancelClick);
    el.messageInput.addEventListener('input', this._handleMessageInput);
    el.categorySelect.addEventListener('change', this._handleCategoryChange);
    el.form.addEventListener('submit', this._handleFormSubmit);
    el.retryButton.addEventListener('click', this._handleRetryClick);
    el.copyTicketIdButton.addEventListener('click', this._handleCopyTicketIdClick);
    el.doneButton.addEventListener('click', this._handleDoneClick);
    el.diagnosticCheckbox.addEventListener('change', () => {
      this.updateDiagnosticPreview();
    });
  }

  private unbindEventListeners(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this._handleDocumentKeyDown);
      document.removeEventListener('ctt:open-feedback', this._handleOpenFeedbackEvent);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('ctt:open-feedback', this._handleOpenFeedbackEvent);
    }

    if (this._elements) {
      this._elements.dialog.removeEventListener('keydown', this._handleDialogKeyDown);
      this._elements.overlay.removeEventListener('click', this._handleOverlayClick);
      this._elements.closeButton.removeEventListener('click', this._handleCloseClick);
      this._elements.cancelButton.removeEventListener('click', this._handleCancelClick);
      this._elements.messageInput.removeEventListener('input', this._handleMessageInput);
      this._elements.categorySelect.removeEventListener('change', this._handleCategoryChange);
      this._elements.form.removeEventListener('submit', this._handleFormSubmit);
      this._elements.retryButton.removeEventListener('click', this._handleRetryClick);
      this._elements.copyTicketIdButton.removeEventListener('click', this._handleCopyTicketIdClick);
      this._elements.doneButton.removeEventListener('click', this._handleDoneClick);
    }
  }

  private handleFocusTrap(evt: KeyboardEvent): void {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const activeEl =
      (this.shadowRoot?.activeElement as HTMLElement | null) ??
      (document.activeElement as HTMLElement | null);

    if (evt.shiftKey) {
      if (activeEl === first) {
        evt.preventDefault?.();
        last.focus();
      }
    } else {
      if (activeEl === last) {
        evt.preventDefault?.();
        first.focus();
      }
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const el = this.elements;

    if (el.successPane.style.display !== 'none') {
      const successCandidates: HTMLElement[] = [
        el.closeButton,
        el.copyTicketIdButton,
        el.doneButton,
      ];
      return successCandidates.filter(
        (item) => !item.hasAttribute('disabled') && item.style.display !== 'none',
      );
    }

    const candidates: HTMLElement[] = [
      el.closeButton,
      el.categorySelect,
      el.prioritySelect,
      el.messageInput,
      el.emailInput,
      el.diagnosticCheckbox,
      el.submitButton,
      el.cancelButton,
    ];

    return candidates.filter(
      (item) => !item.hasAttribute('disabled') && item.style.display !== 'none',
    );
  }

  private updateCharCounterAndValidation(): void {
    const el = this.elements;
    const len = el.messageInput.value.length;
    el.charCounter.textContent = `${len} / ${MAX_FEEDBACK_MESSAGE_LENGTH}`;

    const hasCategory = Boolean(el.categorySelect.value.trim());
    const hasMessage = el.messageInput.value.trim().length >= MIN_FEEDBACK_MESSAGE_LENGTH;

    if (hasCategory && hasMessage) {
      el.submitButton.removeAttribute('disabled');
    } else {
      el.submitButton.setAttribute('disabled', 'true');
    }
  }

  private updateDiagnosticPreview(): void {
    const el = this.elements;
    if (el.diagnosticCheckbox.checked) {
      const diag = this.harvestDiagnosticContext();
      el.diagPreview.textContent = JSON.stringify(diag, null, 2);
    } else {
      el.diagPreview.textContent = '(Technical details omitted)';
    }
  }

  private disableFormControls(): void {
    const el = this.elements;
    el.categorySelect.setAttribute('disabled', 'true');
    el.prioritySelect.setAttribute('disabled', 'true');
    el.messageInput.setAttribute('disabled', 'true');
    el.emailInput.setAttribute('disabled', 'true');
    el.diagnosticCheckbox.setAttribute('disabled', 'true');
    el.submitButton.setAttribute('disabled', 'true');
    el.cancelButton.setAttribute('disabled', 'true');
  }

  private enableFormControls(): void {
    const el = this.elements;
    el.categorySelect.removeAttribute('disabled');
    el.prioritySelect.removeAttribute('disabled');
    el.messageInput.removeAttribute('disabled');
    el.emailInput.removeAttribute('disabled');
    el.diagnosticCheckbox.removeAttribute('disabled');
    el.cancelButton.removeAttribute('disabled');
    this.updateCharCounterAndValidation();
  }
}

// Register custom element if running in an environment with CustomElementRegistry
if (typeof customElements !== 'undefined' && !customElements.get(FEEDBACK_WIDGET_TAG_NAME)) {
  customElements.define(FEEDBACK_WIDGET_TAG_NAME, CttFeedbackWidget);
}

// Automatically record in-app page navigation on initial bundle execution
if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
  recordPageNavigation();
}

export {
  FEEDBACK_CATEGORIES,
  FEEDBACK_PRIORITIES,
  DEFAULT_FEEDBACK_PRIORITY,
  MIN_FEEDBACK_MESSAGE_LENGTH,
  MAX_FEEDBACK_MESSAGE_LENGTH,
};
